import {gql} from "graphql-request";

export const UpdateUser = gql`
  mutation UpdateUser($user:UpdateUserInput!) {
    updateUser(input: $user) {
      id
      displayName
      fullName
      email
      avatar {
        lgUrl
        metadata
      }
      measurementPreference
      mapOrientation
      overZoom
      unitPreference
      hikeTime
      hikeTimeDistance
      hikeTimeElevation
      emailNotification
      pushNotification
      routeStyleSettings {
        routeType
        color
        thicknessMultiplier
        opacity
        hideMileMarkers
      }
      hikeListOverlaySettings {
        active
        selectedIds
      }
    }
  }
`;

export const GetUser = gql`
  query GetUser($firebaseID:String!) {
    user(firebaseUserId:$firebaseID){
      id
      title
      userJwt
      fullName
      displayName
      email
      country {
        id
        name
      }
      avatar {
        mdUrl
        metadata
      }
      appSettings {
        name
        active
      }
      admin
      likesDayTrails
      likesMultidayTrails
      likesOvernightTrails
      viewedTrails {
        id
        trail {
          id
          name
          jumboImage {
            id
            url
            metadata
          }
          trailDistance
          totalElevation
          difficulty
          averageRating
          numberOfRatings
          durationString
          shortAddress
        }
      }
      measurementPreference
      mapOrientation
      overZoom
      unitPreference
      hikeTime
      hikeTimeDistance
      hikeTimeElevation
      gridReference
      emailNotification
      pushNotification
      routeStyleSettings {
        routeType
        color
        thicknessMultiplier
        opacity
        hideMileMarkers
      }
      hikeListOverlaySettings {
        active
        selectedIds
      }
    }
  }
`;

export const GetUserTrailLists = gql`
  query GetUserTrailLists {
    userTrailLists {
      id
      name
          createdAt
      trails {
        id
        name
        jumboImage {
          id
          url
          metadata
        }
        trailDistance
        totalElevation
        difficulty
        averageRating
        numberOfRatings
        durationString
        shortAddress
      }
    }
  }
`;

export const CreateTrailList = gql`
  mutation createTrailList($trailList:CreateTrailListInput!) {
    createTrailList(input:$trailList) {
      id
      name
      errors {
        fieldName
        errors
      }
    }
  }
`;


export const UpdateTrailList = gql`
  mutation updateTrailList($trailList:UpdateTrailListInput!) {
    updateTrailList(input:$trailList) {
      id
      errors {
        fieldName
        errors
      }
    }
  }
`;

export const DestroyTrailList = gql`
  mutation destroyTrailList($trailList:DestroyTrailListInput!) {
    destroyTrailList(input:$trailList) {
      id
      destroyed
      errors {
        fieldName
        errors
      }
    }
  }
`;

// Only consumed by the Save to list dialog, which renders each list's name, its
// picture, "N hikes · Only you / Everyone" and whether the current hike is a
// member — so only hike ids are fetched — plus `kind`, so Completed and
// Favourites sort first, can't be deleted and Completed can ask for a date. The
// full per-hike card payload this used to pull made opening the modal slow.
// Paged: the server caps a page at 20, and the dialog fetches every page.
export const GetUserHikeLists = gql`
  query GetUserHikeLists($page: Int, $perPage: Int) {
    userHikeLists(page: $page, perPage: $perPage) {
      id
      name
      kind
      publiclyVisible
      hikesCount
      hikes {
        id
      }
      images {
        file {
          smUrl
        }
      }
    }
  }
`;

export const GetUserHomepageableHikeLists = gql`
  query GetUserHomepageableHikeLists {
    userHomepageableHikeLists {
      id
      name
      description
      homepageable
      publiclyVisible
      hikesCount
      createdAt
      hikes {
        id
        name
        description
        difficulty
        slug
        mainCountry {
          slug
        }
        mainRegion {
          slug
        }
        distanceMeters
        elevationGainMeters
        elevationLossMeters
        primaryImageUrl
        averageRating
        ratingsCount
      }
    }
  }
`;

export const CreateHikeList = gql`
  mutation createHikeList($input: CreateHikeListInput!) {
    createHikeList(input: $input) {
      hikeList {
        id
        name
        description
        publiclyVisible
        hikesCount
        images {
          file {
            smUrl
          }
        }
      }
      errors {
        fieldName
        errors
      }
    }
  }
`;

// Deleting a list from the Save to list dialog (only lists the user made; the server
// refuses Completed and Favourites).
export const DeleteHikeList = gql`
  mutation deleteHikeList($input: DeleteHikeListInput!) {
    deleteHikeList(input: $input) {
      success
      errors {
        fieldName
        errors
      }
    }
  }
`;

export const AddHikeToList = gql`
  mutation addHikeToList($input: AddHikeToListInput!) {
    addHikeToList(input: $input) {
      hikeList {
        id
        name
        hikesCount
      }
      hikesHikeList {
        id
        position
        notes
      }
      errors {
        fieldName
        errors
      }
    }
  }
`;

// Ticking the Completed list: completes the hike on the day picked, and the server adds it
// to the Completed list itself (CompletedHike after_create).
export const CreateCompletedHike = gql`
  mutation createCompletedHike($input: CreateCompletedHikeInput!) {
    createCompletedHike(input: $input) {
      completedHike {
        id
        completedAt
      }
      errors {
        fieldName
        errors
      }
    }
  }
`;

export const RemoveHikeFromList = gql`
  mutation removeHikeFromList($input: RemoveHikeFromListInput!) {
    removeHikeFromList(input: $input) {
      hikeList {
        id
        name
        hikesCount
      }
      success
      errors {
        fieldName
        errors
      }
    }
  }
`;

// Presign an avatar upload. Same mutation the comments and planned-trail
// uploads use; each domain file keeps its own copy of the document, which is
// the existing convention in this directory.
export const GenerateDirectUpload = gql`
  mutation GenerateDirectUpload($input: GenerateDirectUploadInput!) {
    generateDirectUpload(input: $input) {
      uploadUrl
      headers
      signedId
    }
  }
`;
